import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../../redux/store";
import { fetchPages, upsertPage } from "../../../../redux/blogSlice";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bold, Italic, Underline, Link, List, Minus,
  Eye, EyeOff, CheckCircle, AlertCircle, Loader2, X,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────
   FONTS
───────────────────────────────────────────────────────── */
const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700&family=Montserrat:wght@300;400;500;600;700&display=swap');
`;

/* ─────────────────────────────────────────────────────────
   THEME
───────────────────────────────────────────────────────── */
const C = {
  paper:        "#ffffff",
  surface:      "#f7f7f7",
  ink:          "#0a0a0a",
  inkSoft:      "#2a2a2a",
  inkMid:       "#6b6b6b",
  inkLight:     "#a3a3a3",
  border:       "#e0e0e0",
  success:      "#1a7a3a",
  successBg:    "#f0faf4",
  successBorder:"#b8dfc8",
  danger:       "#b22222",
  dangerBg:     "#fff5f5",
  dangerBorder: "#f0c8c8",
  serif:        "'Cormorant Garamond', Georgia, serif",
  sans:         "'Montserrat', sans-serif",
};

/* ─────────────────────────────────────────────────────────
   RICH TEXT RENDERER  (preview mode)
───────────────────────────────────────────────────────── */
const renderRichLine = (text: string, key: number | string): React.ReactNode => {
  const regex = /(\*\*(.+?)\*\*|_(.+?)_|\[(.+?)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s]+)|([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}))/g;
  const parts: React.ReactNode[] = [];
  let last = 0; let m: RegExpExecArray | null; let k = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(<span key={k++}>{text.slice(last, m.index)}</span>);
    if (m[2])      parts.push(<strong key={k++} style={{ fontWeight:600, color:C.ink }}>{m[2]}</strong>);
    else if (m[3]) parts.push(<em key={k++} style={{ fontStyle:"italic" }}>{m[3]}</em>);
    else if (m[4]) parts.push(<a key={k++} href={m[5]} target="_blank" rel="noopener noreferrer" style={linkSt}>{m[4]}</a>);
    else if (m[6]) parts.push(<a key={k++} href={m[6]} target="_blank" rel="noopener noreferrer" style={{ ...linkSt, wordBreak:"break-all", fontSize:11 }}>{m[6]}</a>);
    else if (m[7]) parts.push(<a key={k++} href={`mailto:${m[7]}`} style={linkSt}>{m[7]}</a>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(<span key={k++}>{text.slice(last)}</span>);
  return <React.Fragment key={key}>{parts.length ? parts : text}</React.Fragment>;
};
const linkSt: React.CSSProperties = { color:C.ink, fontWeight:500, textDecoration:"underline", textUnderlineOffset:3, textDecorationColor:"#cacaca" };

const renderRichContent = (raw: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  const lines = raw.split("\n");
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]; const trimmed = line.trim();
    if (!trimmed) { nodes.push(<div key={`sp${i}`} style={{ height:8 }}/>); i++; continue; }
    if (/^#\s/.test(line)) { nodes.push(<h2 key={`h1${i}`} style={{ fontFamily:C.serif, fontSize:"clamp(22px,3vw,30px)", fontWeight:600, color:C.ink, lineHeight:1.15, margin:"20px 0 8px", letterSpacing:"-0.01em" }}>{renderRichLine(line.replace(/^#+\s/,""),`h${i}`)}</h2>); i++; continue; }
    if (/^#{2}\s/.test(line)) { nodes.push(<h3 key={`h2${i}`} style={{ fontFamily:C.serif, fontSize:"clamp(18px,2.5vw,24px)", fontWeight:600, color:C.ink, lineHeight:1.2, margin:"16px 0 6px" }}>{renderRichLine(line.replace(/^#+\s/,""),`h2${i}`)}</h3>); i++; continue; }
    if (/^[-*•]\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*•]\s/.test(lines[i].trim())) { items.push(lines[i].replace(/^[-*•]\s/,"")); i++; }
      nodes.push(<ul key={`ul${i}`} style={{ margin:"8px 0 12px", paddingLeft:0, listStyle:"none", display:"flex", flexDirection:"column", gap:6 }}>{items.map((item,idx) => <li key={idx} style={{ display:"flex", gap:10, alignItems:"flex-start" }}><span style={{ width:4, height:4, borderRadius:"50%", background:C.inkMid, marginTop:9, flexShrink:0 }}/><span style={{ fontSize:13, lineHeight:1.75, color:C.inkSoft, fontFamily:C.sans, fontWeight:300 }}>{renderRichLine(item,`li${idx}`)}</span></li>)}</ul>);
      continue;
    }
    if (/^---+$/.test(trimmed)) { nodes.push(<div key={`hr${i}`} style={{ height:1, background:C.border, margin:"16px 0" }}/>); i++; continue; }
    if (/^>\s/.test(line)) { nodes.push(<blockquote key={`bq${i}`} style={{ borderLeft:`2px solid ${C.ink}`, margin:"12px 0", paddingLeft:14, paddingTop:2, paddingBottom:2 }}><p style={{ fontFamily:C.serif, fontSize:16, fontStyle:"italic", color:C.inkSoft, lineHeight:1.7, margin:0 }}>{renderRichLine(line.replace(/^>\s/,""),`bq${i}`)}</p></blockquote>); i++; continue; }
    const paraLines: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^[#>\-*•]/.test(lines[i]) && !/^---+$/.test(lines[i].trim())) { paraLines.push(lines[i]); i++; }
    if (paraLines.length) nodes.push(<p key={`p${i}`} style={{ fontSize:13, lineHeight:1.85, color:C.inkSoft, fontFamily:C.sans, fontWeight:300, margin:"0 0 10px" }}>{paraLines.map((pl,pi) => <React.Fragment key={pi}>{renderRichLine(pl,`pl${i}-${pi}`)}{pi<paraLines.length-1&&" "}</React.Fragment>)}</p>);
  }
  return nodes;
};

/* ─────────────────────────────────────────────────────────
   TOOLBAR BUTTON
───────────────────────────────────────────────────────── */
const TBtn: React.FC<{ title:string; onClick:(e:React.MouseEvent)=>void; active?:boolean; children:React.ReactNode }> = ({ title, onClick, active, children }) => (
  <button title={title} onMouseDown={e => { e.preventDefault(); onClick(e); }}
    style={{ width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", transition:"all 0.15s", flexShrink:0, background:active?C.ink:"transparent", color:active?"#fff":C.inkMid, border:`1px solid ${active?C.ink:C.border}` }}
    className="ep-tbtn">
    {children}
  </button>
);
const TDivider = () => <div style={{ width:1, height:20, background:C.border, margin:"0 2px", flexShrink:0 }}/>;

/* ─────────────────────────────────────────────────────────
   RICH EDITOR
───────────────────────────────────────────────────────── */
const RichEditor: React.FC<{ value:string; onChange:(v:string)=>void }> = ({ value, onChange }) => {
  const taRef   = useRef<HTMLTextAreaElement>(null);
  const linkRef = useRef<HTMLInputElement>(null);
  const [preview,   setPreview]   = useState(false);
  const [linkPopup, setLinkPopup] = useState(false);
  const [linkUrl,   setLinkUrl]   = useState("");

  const wrap = (before:string, after:string, sample:string) => {
    const ta=taRef.current; if(!ta) return;
    const s=ta.selectionStart; const e=ta.selectionEnd;
    const sel=value.slice(s,e)||sample;
    onChange(value.slice(0,s)+before+sel+after+value.slice(e));
    setTimeout(()=>{ ta.focus(); ta.setSelectionRange(s+before.length,s+before.length+sel.length); },10);
  };
  const insertPrefix = (prefix:string) => {
    const ta=taRef.current; if(!ta) return;
    const s=ta.selectionStart;
    const ls=value.lastIndexOf("\n",s-1)+1;
    onChange(value.slice(0,ls)+prefix+value.slice(ls));
    setTimeout(()=>{ ta.focus(); ta.setSelectionRange(s+prefix.length,s+prefix.length); },10);
  };
  const insertLink = () => {
    if(!linkUrl.trim()) return;
    const ta=taRef.current; if(!ta) return;
    const s=ta.selectionStart; const e=ta.selectionEnd;
    const label=value.slice(s,e)||"link text";
    onChange(value.slice(0,s)+`[${label}](${linkUrl})`+value.slice(e));
    setLinkUrl(""); setLinkPopup(false);
    setTimeout(()=>taRef.current?.focus(),10);
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:3, padding:"7px 10px", background:C.surface, border:`1px solid ${C.border}`, borderBottom:"none" }}>
        <TBtn title="Bold (**text**)"    onClick={()=>wrap("**","**","bold text")}><Bold size={12}/></TBtn>
        <TBtn title="Italic (_text_)"    onClick={()=>wrap("_","_","italic text")}><Italic size={12}/></TBtn>
        <TBtn title="Underline"          onClick={()=>wrap("<u>","</u>","underline")}><Underline size={12}/></TBtn>
        <TDivider/>
        <TBtn title="H2"                 onClick={()=>insertPrefix("## ")}><span style={{ fontSize:9, fontWeight:700 }}>H2</span></TBtn>
        <TBtn title="H3"                 onClick={()=>insertPrefix("### ")}><span style={{ fontSize:9, fontWeight:700 }}>H3</span></TBtn>
        <TBtn title="Bullet"             onClick={()=>insertPrefix("- ")}><List size={12}/></TBtn>
        <TBtn title='Blockquote (> ...)' onClick={()=>insertPrefix("> ")}><span style={{ fontSize:11, fontStyle:"italic" }}>"</span></TBtn>
        <TBtn title="Divider (---)"      onClick={()=>{ onChange(value+"\n---\n"); setTimeout(()=>taRef.current?.focus(),10); }}><Minus size={12}/></TBtn>
        <TDivider/>
        {/* Link popup */}
        <div style={{ position:"relative" }}>
          <TBtn title="Insert link" onClick={()=>{ setLinkPopup(v=>!v); setTimeout(()=>linkRef.current?.focus(),40); }} active={linkPopup}><Link size={12}/></TBtn>
          <AnimatePresence>
            {linkPopup && (
              <motion.div initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}
                style={{ position:"absolute", top:"110%", left:0, zIndex:200, display:"flex", gap:6, alignItems:"center", background:C.paper, border:`1px solid ${C.border}`, padding:"7px 10px", boxShadow:"0 4px 20px rgba(0,0,0,0.08)", minWidth:260 }}>
                <input ref={linkRef} type="url" placeholder="https://example.com" value={linkUrl}
                  onChange={e=>setLinkUrl(e.target.value)} onKeyDown={e=>e.key==="Enter"&&insertLink()}
                  style={{ flex:1, background:C.surface, border:`1px solid ${C.border}`, padding:"7px 10px", fontSize:11, color:C.ink, fontFamily:C.sans, outline:"none" }}/>
                <button onClick={insertLink} style={{ background:C.ink, color:"#fff", border:"none", padding:"7px 12px", fontSize:9, fontWeight:700, letterSpacing:"0.12em", cursor:"pointer" }}>OK</button>
                <button onClick={()=>setLinkPopup(false)} style={{ background:"transparent", border:`1px solid ${C.border}`, width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:C.inkMid }}><X size={11}/></button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div style={{ flex:1 }}/>
        <TBtn title={preview?"Edit":"Preview"} onClick={()=>setPreview(v=>!v)} active={preview}>
          {preview ? <EyeOff size={12}/> : <Eye size={12}/>}
        </TBtn>
      </div>

      {/* Editor / Preview */}
      {preview ? (
        <div style={{ minHeight:200, padding:"16px 14px", border:`1px solid ${C.border}`, background:C.paper, overflowY:"auto" }}>
          {value.trim() ? renderRichContent(value) : <span style={{ fontSize:11, color:C.inkLight, fontStyle:"italic" }}>Nothing to preview yet…</span>}
        </div>
      ) : (
        <textarea ref={taRef} value={value} onChange={e=>onChange(e.target.value)}
          placeholder={"Write your page content here…\n\n## Section Heading\n\nParagraph with **bold**, _italic_, or [links](https://).\n\n- Bullet point"}
          style={{ width:"100%", minHeight:240, background:C.paper, border:`1px solid ${C.border}`, padding:"14px", fontSize:13, color:C.ink, lineHeight:1.75, fontFamily:C.sans, fontWeight:300, resize:"vertical", outline:"none", boxSizing:"border-box" as const, transition:"border-color 0.2s" }}
          className="ep-input"
        />
      )}

      {/* Syntax hints */}
      <div style={{ display:"flex", gap:10, flexWrap:"wrap", padding:"5px 0 0" }}>
        {[["**bold**","bold"],["_italic_","italic"],["[text](url)","link"],["## H2","heading"],["- item","bullet"]].map(([syn,lbl]) => (
          <span key={lbl} style={{ fontSize:9, color:C.inkLight }}>
            <code style={{ background:C.surface, padding:"1px 4px", fontFamily:"monospace", fontSize:9 }}>{syn}</code> {lbl}
          </span>
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN
════════════════════════════════════════════════════════════ */
const EditPage: React.FC = () => {
  const { pageId } = useParams<{ pageId: string }>();
  const dispatch   = useDispatch<AppDispatch>();
  const pages      = useSelector((state: RootState) => state.blog.pages);

  /* ── original state ── */
  const [title,   setTitle]   = useState("");
  const [slug,    setSlug]    = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);

  /* ── extra UI state ── */
  const [slugEdited, setSlugEdited] = useState(false);
  const [toast, setToast] = useState<{ type:"success"|"danger"; text:string } | null>(null);

  const page = pages.find((p) => p.id === pageId);

  useEffect(() => { dispatch(fetchPages()); }, [dispatch]);

  /* ── original: populate fields when page loads ── */
  useEffect(() => {
    if (page) {
      setTitle(page.title);
      setSlug(page.slug);
      setContent(page.content);
    }
  }, [page]);

  const generateSlug = (text: string) =>
    text.toLowerCase().trim().replace(/[^\w\s-]/g,"").replace(/\s+/g,"-");

  const flash = (type:"success"|"danger", text:string) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  /* ── original: handleSubmit ── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { flash("danger","Please add a page title."); return; }
    setLoading(true);
    try {
      await dispatch(upsertPage({ id: pageId, title, slug, content })).unwrap();
      flash("success", "Page updated successfully!");
    } catch (err: any) {
      flash("danger", err?.message || "Failed to update page.");
    }
    setLoading(false);
  };

  /* ── Loading state ── */
  if (!page) {
    return (
      <div style={{ background:C.paper, fontFamily:C.sans }}>
        <style>{FONT_CSS}</style>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:240, gap:10 }}>
          <div style={{ width:32, height:1, background:C.border }}/>
          <p style={{ fontSize:11, color:C.inkLight, letterSpacing:"0.2em" }}>LOADING PAGE…</p>
        </div>
      </div>
    );
  }

  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;

  return (
    <div style={{ background:C.paper, fontFamily:C.sans, color:C.ink }}>
      <style>{FONT_CSS + EP_CSS}</style>

      {/* ── TITLE STRIP ── */}
      <div style={S.titleStrip}>
        <div style={{ height:2, background:C.ink, marginBottom:16 }}/>
        <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", flexWrap:"wrap", gap:8, marginBottom:16 }}>
          <div>
            <p style={S.eye}>CONTENT MANAGEMENT</p>
            <h1 style={S.title}>Edit Page</h1>
          </div>
          <div style={{ display:"flex", gap:8, paddingBottom:4 }}>
            <span style={S.pill}>{wordCount} words</span>
            <span style={S.pill} title={page.slug}>/{page.slug}</span>
          </div>
        </div>
        <div style={{ height:1, background:C.border }}/>
      </div>

      <div style={S.body}>

        {/* ── TOAST ── */}
        <AnimatePresence>
          {toast && (
            <motion.div key="toast"
              initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }}
              exit={{ opacity:0, y:-8 }} transition={{ duration:0.22 }}
              style={{ display:"flex", alignItems:"center", gap:10, padding:"11px 16px", marginBottom:20, background:toast.type==="success"?C.successBg:C.dangerBg, border:`1px solid ${toast.type==="success"?C.successBorder:C.dangerBorder}` }}>
              {toast.type==="success"
                ? <CheckCircle size={13} color={C.success}/>
                : <AlertCircle size={13} color={C.danger}/>}
              <span style={{ fontSize:11, fontWeight:500, flex:1, color:toast.type==="success"?C.success:C.danger }}>
                {toast.text}
              </span>
              <button onClick={()=>setToast(null)} style={{ background:"none", border:"none", cursor:"pointer", color:C.inkMid }}><X size={11}/></button>
            </motion.div>
          )}
        </AnimatePresence>

        <form onSubmit={handleSubmit} style={S.form}>

          <div className="ep-layout">

            {/* ── LEFT ── */}
            <div style={{ display:"flex", flexDirection:"column", gap:20 }}>

              {/* Title */}
              <div style={S.field}>
                <label style={S.label}>PAGE TITLE <span style={{ color:C.danger }}>*</span></label>
                <input type="text" placeholder="e.g. About Us"
                  value={title}
                  onChange={e => { setTitle(e.target.value); if (!slugEdited) setSlug(generateSlug(e.target.value)); }}
                  style={S.titleInput} className="ep-input ep-title"/>
              </div>

              {/* Slug */}
              <div style={S.field}>
                <label style={S.label}>URL SLUG</label>
                <div style={S.slugWrap}>
                  <span style={S.slugPrefix}>site.com/</span>
                  <input type="text" placeholder="e.g. about-us"
                    value={slug}
                    onChange={e => { setSlug(e.target.value.toLowerCase().replace(/[^\w-]/g,"-")); setSlugEdited(true); }}
                    style={S.slugInput} className="ep-input"/>
                  {slugEdited && (
                    <button type="button"
                      onClick={()=>{ setSlug(generateSlug(title)); setSlugEdited(false); }}
                      style={S.slugReset}>Reset</button>
                  )}
                </div>
              </div>

              {/* Content */}
              <div style={S.field}>
                <label style={S.label}>CONTENT</label>
                <RichEditor value={content} onChange={setContent}/>
              </div>

              {/* Submit */}
              <motion.button
                whileTap={{ scale:0.97 } as any}
                type="submit" disabled={loading}
                style={{ ...S.submitBtn, opacity:loading?0.65:1, cursor:loading?"not-allowed":"pointer" }}
                className="ep-submit">
                {loading
                  ? <><Loader2 size={14} className="ep-spin" style={{ marginRight:8 }}/> UPDATING…</>
                  : "PUBLISH UPDATES"
                }
              </motion.button>

            </div>

            {/* ── RIGHT SIDEBAR ── */}
            <div style={S.sidebar}>

              {/* Page info */}
              <div style={S.sideCard}>
                <p style={S.sideCardTitle}>PAGE DETAILS</p>
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {[
                    { label:"Page ID",  value:pageId||"—" },
                    { label:"Slug",     value:`/${page.slug}` },
                    { label:"Words",    value:String(wordCount) },
                    { label:"Content",  value:`${content.length} chars` },
                  ].map(row => (
                    <div key={row.label} style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", paddingBottom:8, borderBottom:`1px solid ${C.border}` }}>
                      <span style={{ fontSize:9, color:C.inkMid, letterSpacing:"0.1em" }}>{row.label}</span>
                      <span style={{ fontSize:10, color:C.ink, fontFamily:"'Courier New',monospace", textAlign:"right" as const, maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" as const }}>{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Checklist */}
              <div style={S.sideCard}>
                <p style={S.sideCardTitle}>UPDATE CHECKLIST</p>
                <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
                  {[
                    { label:"Title present",   done:title.trim().length>0 },
                    { label:"Slug configured", done:slug.trim().length>0 },
                    { label:"Content written", done:wordCount>0 },
                    { label:"Min 50 words",    done:wordCount>=50 },
                  ].map(item => (
                    <div key={item.label} style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <div style={{ width:14, height:14, borderRadius:"50%", flexShrink:0, background:item.done?C.ink:"transparent", border:`1.5px solid ${item.done?C.ink:C.border}`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                        {item.done && <div style={{ width:4, height:4, borderRadius:"50%", background:"#fff" }}/>}
                      </div>
                      <span style={{ fontSize:10, color:item.done?C.ink:C.inkLight }}>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* View link */}
              <button type="button"
                onClick={()=>window.open(`/pages/${page.slug}`,"_blank")}
                style={S.viewBtn}>
                VIEW LIVE PAGE ↗
              </button>

            </div>
          </div>

        </form>
      </div>
    </div>
  );
};

export default EditPage;

/* ─────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────── */
const S: { [key: string]: React.CSSProperties } = {
  titleStrip: { padding:"20px 20px 0" },
  eye:   { fontSize:9, letterSpacing:"0.3em", color:C.inkMid, fontFamily:C.sans, marginBottom:6 },
  title: { fontFamily:C.serif, fontSize:"clamp(26px,4vw,42px)" as any, fontWeight:600, color:C.ink, lineHeight:1, margin:0, letterSpacing:"-0.01em" },
  pill:  { fontSize:9, letterSpacing:"0.14em", color:C.inkMid, border:`1px solid ${C.border}`, padding:"3px 10px", fontFamily:C.sans },

  body:  { maxWidth:1160, margin:"0 auto", padding:"28px 20px 60px" },
  form:  { width:"100%" },

  field:      { display:"flex", flexDirection:"column", gap:7 },
  label:      { fontSize:9, letterSpacing:"0.22em", color:C.inkMid, fontFamily:C.sans, fontWeight:600 },
  titleInput: { width:"100%", background:C.paper, border:`1px solid ${C.border}`, padding:"14px 16px", fontSize:20, fontWeight:600, color:C.ink, fontFamily:C.serif, outline:"none", boxSizing:"border-box" as const, letterSpacing:"-0.01em", transition:"border-color 0.2s" },
  slugWrap:   { display:"flex", alignItems:"center", border:`1px solid ${C.border}`, background:C.paper },
  slugPrefix: { fontSize:11, color:C.inkLight, padding:"0 12px", flexShrink:0, borderRight:`1px solid ${C.border}`, lineHeight:"42px" },
  slugInput:  { flex:1, background:"transparent", border:"none", outline:"none", padding:"11px 12px", fontSize:12, color:C.ink, fontFamily:"'Courier New',monospace", letterSpacing:"0.02em" },
  slugReset:  { fontSize:9, color:C.inkMid, letterSpacing:"0.1em", background:"none", border:"none", cursor:"pointer", padding:"0 12px", fontFamily:C.sans, flexShrink:0 },

  submitBtn: { display:"inline-flex", alignItems:"center", justifyContent:"center", background:C.ink, color:"#fff", border:"none", padding:"13px 28px", fontSize:10, fontWeight:700, letterSpacing:"0.22em", fontFamily:C.sans, width:"100%", transition:"opacity 0.2s" },

  sidebar:      { position:"sticky" as const, top:20, display:"flex", flexDirection:"column", gap:14 },
  sideCard:     { border:`1px solid ${C.border}`, padding:"15px 16px" },
  sideCardTitle:{ fontSize:9, letterSpacing:"0.25em", color:C.ink, fontFamily:C.sans, fontWeight:700, marginBottom:12 },
  viewBtn: {
    display:"block", width:"100%", background:"transparent",
    border:`1px solid ${C.border}`, color:C.inkMid,
    padding:"11px 0", fontSize:9, fontWeight:600,
    letterSpacing:"0.18em", cursor:"pointer",
    fontFamily:C.sans, textAlign:"center" as const,
    transition:"border-color 0.18s, color 0.18s",
  },
};

const EP_CSS = `
  * { box-sizing: border-box; }
  ::-webkit-scrollbar { width: 0; }
  .ep-input:focus  { border-color: #0a0a0a !important; }
  .ep-title:focus  { border-color: #0a0a0a !important; }
  input::placeholder  { color: #a3a3a3; font-size: 12px; }
  textarea::placeholder { color: #a3a3a3; font-size: 12px; line-height: 1.75; }
  .ep-tbtn:hover { border-color: #0a0a0a !important; color: #0a0a0a !important; }

  @keyframes ep-spin { from{transform:rotate(0deg);} to{transform:rotate(360deg);} }
  .ep-spin { animation: ep-spin 1s linear infinite; }

  .ep-layout {
    display: grid;
    grid-template-columns: 1fr 280px;
    gap: 32px;
    align-items: start;
  }
  @media (max-width: 720px) {
    .ep-layout { grid-template-columns: 1fr !important; gap: 24px !important; }
    .ep-layout > div:last-child { position: relative !important; top: 0 !important; }
    .ep-submit { width: 100% !important; }
  }
`;